import { aggregateMetricsData } from 'src/utilities/CommonUtilities';
import { TicketAggregatedData, TicketsBaseMetricData, TicketsParsedDataItem } from './TicketsInterfaces';
import {
  prepareTicketDataForAggregation,
  aggregateResolutionTypeCounts
} from '../business-reporting/business-reporting-aggregator-utils/BusinessReportingTicketsAggregatorUtils';
import { TICKETS_COLUMN_LIST, TICKETS_MEASURE_COLUMN_LIST } from './TicketsConstants';

/**
 * Finds the most common resolution type from resolution counts
 */
const findMostCommonResolution = (resolutionCounts: Record<string, number>): string => {
  let mostCommonResolution = 'None';
  let maxCount = 0;

  Object.entries(resolutionCounts).forEach(([resolution, count]) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommonResolution = resolution;
    }
  });

  return mostCommonResolution;
};

/**
 * Maps base metrics to the specific format needed by getTicketData
 */
const mapToTicketDataFormat = (
  baseMetrics: TicketsBaseMetricData[],
  resolutionTypeMap: Map<string, Record<string, number>>,
  sev2ResolutionTypeMap: Map<string, Record<string, number>>
): TicketAggregatedData[] => {
  return baseMetrics.map((metric: TicketsBaseMetricData) => {
    const key = `${metric.create_date_UserTimeZone_formatted}-${metric.application_name}`;
    const resolutionCounts = resolutionTypeMap.get(key) || {};
    const sev2ResolutionTypes = sev2ResolutionTypeMap.get(key) || {};

    return {
      month: metric.create_date_UserTimeZone_formatted,
      application_name: metric.application_name,
      total_tickets: metric.total_tickets,
      sev2_tickets: metric.sev2_tickets,
      sev3_tickets: metric.sev3_tickets,
      sev4_tickets: metric.sev4_tickets,
      sev5_tickets: metric.sev5_tickets,
      sev2_resolution_types: sev2ResolutionTypes,
      sev2_time_spent: metric.sev2_time_spent,
      total_time_spent: metric.total_time_spent,
      most_common_resolution: findMostCommonResolution(resolutionCounts)
    };
  });
};

export const getTicketData = (ticketData: TicketsParsedDataItem[]): string => {
  try {
    if (ticketData.length === 0) {
      return JSON.stringify({ entries: [], totalSev2Tickets: 0, totalTicketsSum: 0 });
    }

    const preparedData = prepareTicketDataForAggregation(ticketData);

    const baseMetrics = aggregateMetricsData(TICKETS_COLUMN_LIST, preparedData, TICKETS_MEASURE_COLUMN_LIST);

    if (!baseMetrics || baseMetrics.length === 0) {
      return JSON.stringify({ entries: [], totalSev2Tickets: 0, totalTicketsSum: 0 });
    }

    const { resolutionTypeMap, sev2ResolutionTypeMap } = aggregateResolutionTypeCounts(ticketData);

    // 4. Map to our specific output format
    const finalAggregatedData = mapToTicketDataFormat(baseMetrics, resolutionTypeMap, sev2ResolutionTypeMap);

    const totalSev2Tickets = finalAggregatedData.reduce((sum, entry) => sum + entry.sev2_tickets, 0);
    const totalTicketsSum = finalAggregatedData.reduce((sum, entry) => sum + entry.total_tickets, 0);

    return JSON.stringify({
      entries: finalAggregatedData,
      totalSev2Tickets,
      totalTicketsSum
    });
  } catch (error) {
    console.error('Error in getTicketData aggregation:', error);
    return JSON.stringify({ entries: [], totalSev2Tickets: 0, totalTicketsSum: 0 });
  }
};
